import { Editable } from "../edit";
import { useSiteLink } from "../link";
import type { CtaBandContent, Spec } from "../content/common";

// Shared section primitives used across page components so the markup stays
// identical to the original hardcoded pages. Each takes a `path` — the dot-path
// prefix to the content node it renders — so its fields are click-to-editable in
// the admin preview (inert on the public site, where no edit context exists).

export function PageHero({
  path,
  eyebrow,
  heading,
  lede,
  note,
}: {
  path: string;
  eyebrow: string;
  heading: string;
  lede: string;
  /** When provided (incl. ""), renders a trailing `<p className="small muted">`. */
  note?: string;
}) {
  return (
    <section className="hero page-hero">
      <div className="hero-art" aria-hidden="true" style={{ opacity: 0.55 }}></div>
      <div className="wrap">
        <div className="hero-copy">
          <Editable as="p" className="eyebrow" path={`${path}.eyebrow`} value={eyebrow} />
          <Editable as="h1" path={`${path}.heading`} value={heading} />
          <Editable as="p" className="lede" path={`${path}.lede`} value={lede} singleLine={false} />
          {note !== undefined && (
            <Editable as="p" className="small muted" path={`${path}.note`} value={note} singleLine={false} />
          )}
        </div>
      </div>
    </section>
  );
}

export function SectionHead({
  path,
  eyebrow,
  heading,
  lede,
}: {
  path: string;
  eyebrow: string;
  heading: string;
  lede?: string;
}) {
  return (
    <div className="section-head">
      <Editable as="p" className="eyebrow" path={`${path}.eyebrow`} value={eyebrow} />
      <Editable as="h2" path={`${path}.heading`} value={heading} />
      {lede !== undefined && <Editable as="p" className="lede" path={`${path}.lede`} value={lede} singleLine={false} />}
    </div>
  );
}

export function SpecBlock({ spec, path }: { spec: Spec; path: string }) {
  const itemsGrid = (
    <div className="spec-items">
      {spec.items.map((it, i) => (
        <div key={i}>
          <h4>
            <Editable as="span" path={`${path}.items.${i}.title`} value={it.title} />
            {it.badge && (
              <>
                {" "}
                <Editable as="span" className="pill soon" path={`${path}.items.${i}.badge`} value={it.badge} />
              </>
            )}
          </h4>
          <Editable as="p" path={`${path}.items.${i}.description`} value={it.description} singleLine={false} />
        </div>
      ))}
    </div>
  );

  return (
    <div className="spec" {...(spec.id ? { id: spec.id } : {})}>
      <div className="spec-head">
        <Editable as="p" className="eyebrow" path={`${path}.eyebrow`} value={spec.eyebrow} />
        <Editable as="h2" path={`${path}.heading`} value={spec.heading} />
        {spec.intro !== undefined && <Editable as="p" path={`${path}.intro`} value={spec.intro} singleLine={false} />}
      </div>
      {spec.table ? (
        <div style={{ display: "grid", gap: 28, minWidth: 0 }}>
          {itemsGrid}
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  {spec.table.columns.map((col, i) => (
                    <th scope="col" key={i}>
                      <Editable as="span" path={`${path}.table.columns.${i}`} value={col} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {spec.table.rows.map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => (
                      <td key={j}>
                        <Editable as="span" path={`${path}.table.rows.${i}.${j}`} value={cell} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        itemsGrid
      )}
    </div>
  );
}

export function CtaBandSection({ content, path }: { content: CtaBandContent; path: string }) {
  const Link = useSiteLink();
  return (
    <section className="cta-band">
      <div className="wrap">
        <Editable as="p" className="eyebrow" path={`${path}.eyebrow`} value={content.eyebrow} />
        <Editable as="h2" path={`${path}.heading`} value={content.heading} />
        <Editable as="p" className="lede" path={`${path}.lede`} value={content.lede} singleLine={false} />
        <Link className="btn btn-primary" to={content.buttonTo}>
          <Editable as="span" path={`${path}.buttonLabel`} value={content.buttonLabel} />{" "}
          <span className="arrow" aria-hidden="true">
            →
          </span>
        </Link>
      </div>
    </section>
  );
}
